import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  styled,
  IconButton,
  List,
  ListItem,
  Collapse,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { SimplifiedNavigationTree } from "../NavigationTree";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "./BatchInviteModal.css";
import accessService from "../../../apis/Profile/AccessServiceApi";
import { getTransportCountryData } from "../../../apis/Dropdown";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: "900px",
    width: "100%",
  },
}));

const EnhancedBatchInviteModal = ({ isOpen, onClose }) => {
  const [userEntries, setUserEntries] = useState([
    {
      email: "",
      modules: [],
      permission: "write",
      expanded: true,
      isNewUser: false,
      userDetails: {},
    },
  ]);
  const [period, setPeriod] = useState("yearly");
  const [showPaymentSummary, setShowPaymentSummary] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState(0);
  const [batchId, setBatchId] = useState(null);
  const [dropdownCountry, setDropdownCountry] = useState([]);

  useEffect(() => {
    getTransportCountryData({ setDropdownData: setDropdownCountry });
  }, []);

  // For tab switching between existing/new users
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAddUser = () => {
    setUserEntries([
      ...userEntries,
      {
        email: "",
        modules: [],
        permission: "write",
        expanded: true,
        isNewUser: tabValue === 1,
        userDetails: {
          firstName: "",
          lastName: "",
          phone: "",
          position: "",
          CVRNumber: "",
          country: "",
          sector: "",
          businessName: "",
          businessAddress: "",
        },
      },
    ]);
    setCurrentEditIndex(userEntries.length);
  };

  const handleRemoveUser = (index) => {
    const newEntries = [...userEntries];
    newEntries.splice(index, 1);
    setUserEntries(newEntries);
    if (currentEditIndex >= newEntries.length) {
      setCurrentEditIndex(Math.max(0, newEntries.length - 1));
    }
  };

  const handleEmailChange = (index, value) => {
    const newEntries = [...userEntries];
    newEntries[index].email = value;
    setUserEntries(newEntries);
  };

  const handleUserDetailChange = (index, field, value) => {
    const newEntries = [...userEntries];
    newEntries[index].userDetails = {
      ...newEntries[index].userDetails,
      [field]: value,
    };
    setUserEntries(newEntries);
  };

  const handleToggleExpand = (index) => {
    const newEntries = [...userEntries];
    newEntries[index].expanded = !newEntries[index].expanded;
    setUserEntries(newEntries);
    setCurrentEditIndex(index);
  };

  const handleModuleSelect = (items, permission, bundles) => {
    const newEntries = [...userEntries];
    newEntries[currentEditIndex].modules = items;
    newEntries[currentEditIndex].permission = permission;
    newEntries[currentEditIndex].bundles = bundles;
    setUserEntries(newEntries);
  };

  const handleToggleUserType = (index, isNewUser) => {
    const newEntries = [...userEntries];
    newEntries[index].isNewUser = isNewUser;
    setUserEntries(newEntries);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Filter out empty emails
    const validEntries = userEntries.filter((entry) => entry.email.trim());

    if (validEntries.length === 0) {
      return; // No valid entries
    }

    try {
      // Process existing and new users differently
      const existingUserEmails = validEntries
        .filter((entry) => !entry.isNewUser)
        .map((entry) => entry.email.trim());

      const newUserData = validEntries
        .filter((entry) => entry.isNewUser)
        .map((entry) => ({
          email: entry.email.trim(),
          firstName: entry.userDetails.firstName,
          lastName: entry.userDetails.lastName,
          phone: entry.userDetails.phone,
          position: entry.userDetails.position,
          CVRNumber: entry.userDetails.CVRNumber || "",
          country: entry.userDetails.country || "",
          sector: entry.userDetails.sector || "",
          businessName: entry.userDetails.businessName || "",
          businessAddress: entry.userDetails.businessAddress || "",
        }));

      // Combine all emails for the API call
      const allEmails = [
        ...existingUserEmails,
        ...newUserData.map((user) => user.email),
      ];

      // Since we need to maintain module selection for each user,
      // we'll need to determine if all users have the same modules/permissions
      const allSameModules = validEntries.every(
        (entry) =>
          JSON.stringify(entry.modules) ===
          JSON.stringify(validEntries[0].modules)
      );

      const allSamePermissions = validEntries.every(
        (entry) => entry.permission === validEntries[0].permission
      );

      let batchIds = [];

      if (allSameModules && allSamePermissions) {
        // If all users have the same settings, make a single API call
        const response = await accessService.batchInviteUsers({
          emails: allEmails,
          new_users: newUserData,
          modules: validEntries[0].modules,
          permission: validEntries[0].permission,
          bundles: validEntries[0].bundles,
          period,
        });
        batchIds.push(response.batch_id);
      } else {
        // Otherwise, process each user individually
        for (let i = 0; i < validEntries.length; i++) {
          const entry = validEntries[i];
          const userData = entry.isNewUser
            ? [
                {
                  email: entry.email.trim(),
                  firstName: entry.userDetails.firstName,
                  lastName: entry.userDetails.lastName,
                  phone: entry.userDetails.phone,
                  position: entry.userDetails.position,
                  CVRNumber: entry.userDetails.CVRNumber || "",
                  country: entry.userDetails.country || "",
                  sector: entry.userDetails.sector || "",
                  businessName: entry.userDetails.businessName || "",
                  businessAddress: entry.userDetails.businessAddress || "",
                },
              ]
            : [];

          const result = await accessService.batchInviteUsers({
            emails: [entry.email.trim()],
            new_users: userData,
            modules: entry.modules,
            permission: entry.permission,
            bundles: entry.bundles,
            period,
          });
          batchIds.push(result.batch_id);
        }
      }

      // Store the batch_ids for later use in the checkout session
      setBatchId(batchIds);
      setShowPaymentSummary(true);
    } catch (error) {
      console.error("Error inviting users:", error);
    }
  };

  const handlePayment = async () => {
    try {
      const validUsers = userEntries.filter((entry) => entry.email.trim());

      if (!batchId || batchId.length === 0) {
        console.error("No batch_ids available for checkout");
        return;
      }

      // Count users by bundle type
      const bundleCounts = {};
      validUsers.forEach((user) => {
        const bundle = user.bundles || "freemium";
        bundleCounts[bundle] = (bundleCounts[bundle] || 0) + 1;
      });

      // Convert to array format for API
      const batchData = Object.entries(bundleCounts).map(
        ([bundle, quantity]) => ({
          bundle,
          quantity,
        })
      );

      const { sessionUrl } = await accessService.createCheckoutSession({
        period,
        batch_data: batchData, // Send bundle-specific data
        batch_ids: batchId,
      });

      window.location.href = sessionUrl;
    } catch (error) {
      console.error("Error creating checkout session:", error);
    }
  };

  const getModuleNamesById = (moduleIds) => {
    const allModules = [
      // Dash modules
      { id: 1, name: "Company" },
      { id: 8, name: "Admin" },
      { id: 9, name: "AR" },
      // Regular modules
      { id: 7, name: "Education" },
      { id: 2, name: "Product" },
      { id: 10, name: "Client" },
      { id: 3, name: "Ambition" },
      { id: 4, name: "Report" },
      { id: 11, name: "Share" },
      { id: 5, name: "Map" },
      { id: 6, name: "Feed" },
      { id: 12, name: "Simulate" },
      { id: 13, name: "Get Help" },
      { id: 14, name: "Business" },
      { id: 15, name: "ERP" },
    ];

    return moduleIds.map((id) => {
      const module = allModules.find((m) => m.id === id);
      return module ? module.name : `Module ${id}`;
    });
  };

  const bundlePrices = {
    freemium: { yearly: 0, monthly: 0 },
    together: { yearly: 42000, monthly: 3500 },
    ambition: { yearly: 15000, monthly: 1250 },
    initiator: { yearly: 9000, monthly: 750 },
  };

  const calculateTotalPrice = () => {
    const validUsers = userEntries.filter((user) => user.email.trim());

    return validUsers.reduce((total, user) => {
      const bundle = user.bundles || "freemium";
      return total + bundlePrices[bundle][period];
    }, 0);
  };

  // Render user entry fields based on whether they're new or existing
  const renderUserFields = (entry, index) => {
    if (entry.isNewUser) {
      return (
        <Box className="new-user-fields" sx={{ mt: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <TextField
              value={entry.userDetails.firstName || ""}
              onChange={(e) =>
                handleUserDetailChange(index, "firstName", e.target.value)
              }
              placeholder="First Name"
              fullWidth
              variant="outlined"
              size="small"
              label="First Name"
            />
            <TextField
              value={entry.userDetails.lastName || ""}
              onChange={(e) =>
                handleUserDetailChange(index, "lastName", e.target.value)
              }
              placeholder="Last Name"
              fullWidth
              variant="outlined"
              size="small"
              label="Last Name"
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <TextField
              value={entry.userDetails.phone || ""}
              onChange={(e) =>
                handleUserDetailChange(index, "phone", e.target.value)
              }
              placeholder="Phone Number"
              fullWidth
              variant="outlined"
              size="small"
              label="Phone"
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <TextField
              value={entry.userDetails.position || ""}
              onChange={(e) =>
                handleUserDetailChange(index, "position", e.target.value)
              }
              placeholder="Position"
              fullWidth
              variant="outlined"
              size="small"
              label="Position"
            />
          </Box>

          <Box sx={{ mb: 1 }}>
            <TextField
              value={entry.userDetails.CVRNumber || ""}
              onChange={(e) =>
                handleUserDetailChange(index, "CVRNumber", e.target.value)
              }
              placeholder="VAT Number"
              fullWidth
              variant="outlined"
              size="small"
              label="VAT Number"
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <TextField
              value={entry.userDetails.businessName || ""}
              onChange={(e) =>
                handleUserDetailChange(index, "businessName", e.target.value)
              }
              placeholder="Business Name"
              fullWidth
              variant="outlined"
              size="small"
              label="Business Name"
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <TextField
              value={entry.userDetails.businessAddress || ""}
              onChange={(e) =>
                handleUserDetailChange(index, "businessAddress", e.target.value)
              }
              placeholder="Address"
              fullWidth
              variant="outlined"
              size="small"
              label="Business Address"
            />
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Country</InputLabel>
              <Select
                value={entry.userDetails.country || ""}
                onChange={(e) =>
                  handleUserDetailChange(index, "country", e.target.value)
                }
                label="Country"
              >
                {dropdownCountry.map((country, idx) => (
                  <MenuItem key={idx} value={country.value}>
                    {country.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Sector</InputLabel>
              <Select
                value={entry.userDetails.sector || ""}
                onChange={(e) =>
                  handleUserDetailChange(index, "sector", e.target.value)
                }
                label="Sector"
              >
                <MenuItem value="technology">Technology</MenuItem>
                <MenuItem value="manufacturing">Manufacturing</MenuItem>
                <MenuItem value="services">Services</MenuItem>
                <MenuItem value="retail">Retail</MenuItem>
                <MenuItem value="finance">Finance</MenuItem>
                <MenuItem value="healthcare">Healthcare</MenuItem>
                <MenuItem value="education">Education</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      );
    }

    return null;
  };

  return (
    <StyledDialog open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ color: "#193666" }}>Invite Users</DialogTitle>
      <DialogContent>
        {!showPaymentSummary ? (
          <Box className="invite-container">
            <Box className="left-panel">
              <SimplifiedNavigationTree onSelect={handleModuleSelect} />
            </Box>

            <Box className="right-panel">
              <Box className="user-list-section">
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ color: "#193666" }}
                >
                  Users to Invite
                </Typography>

                <List className="user-list">
                  {userEntries.map((entry, index) => (
                    <Paper
                      key={index}
                      elevation={2}
                      className="user-entry"
                      style={{ marginBottom: "10px", padding: "8px" }}
                    >
                      <ListItem
                        secondaryAction={
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveUser(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <TextField
                          value={entry.email}
                          onChange={(e) =>
                            handleEmailChange(index, e.target.value)
                          }
                          placeholder="Email"
                          fullWidth
                          variant="outlined"
                          size="small"
                          onClick={() => setCurrentEditIndex(index)}
                        />
                        <IconButton onClick={() => handleToggleExpand(index)}>
                          {entry.expanded ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </ListItem>

                      <Collapse
                        in={entry.expanded}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ padding: "8px 16px 16px" }}>
                          {/* Toggle between existing/new user */}
                          <ToggleButtonGroup
                            exclusive
                            value={entry.isNewUser}
                            onChange={(e, value) => {
                              if (value !== null) {
                                handleToggleUserType(index, value);
                              }
                            }}
                            size="small"
                            sx={{ mb: 2, width: "100%" }}
                          >
                            <ToggleButton value={false} sx={{ width: "50%" }}>
                              Existing User
                            </ToggleButton>
                            <ToggleButton value={true} sx={{ width: "50%" }}>
                              New User
                            </ToggleButton>
                          </ToggleButtonGroup>

                          {/* Render registration fields for new users */}
                          {renderUserFields(entry, index)}

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mt: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ color: "#193666" }}
                            >
                              Permission:{" "}
                              <Chip
                                label={
                                  entry.permission === "read"
                                    ? "Read Only"
                                    : "Read + Write"
                                }
                                color={
                                  entry.permission === "read"
                                    ? "primary"
                                    : "secondary"
                                }
                                size="small"
                              />
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{ color: "#193666" }}
                            >
                              {entry.modules.length} modules selected
                            </Typography>
                          </Box>

                          {entry.modules.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: "bold" }}
                              >
                                Selected Modules:
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "4px",
                                  mt: 0.5,
                                }}
                              >
                                {getModuleNamesById(entry.modules).map(
                                  (name, i) => (
                                    <Chip key={i} label={name} size="small" />
                                  )
                                )}
                              </Box>
                            </Box>
                          )}

                          {currentEditIndex === index && (
                            <Typography
                              variant="caption"
                              color="primary"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 1,
                              }}
                            >
                              <CheckCircleIcon
                                fontSize="small"
                                sx={{ mr: 0.5 }}
                              />
                              Currently editing this user's permissions
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Paper>
                  ))}
                </List>

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddUser}
                  fullWidth
                  sx={{ mt: 2, mb: 2, color: "#193666" }}
                >
                  Add Another User
                </Button>

                <Box className="subscription-period">
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "#193666" }}
                  >
                    Subscription Period
                  </Typography>
                  <ToggleButtonGroup
                    value={period}
                    exclusive
                    onChange={(e, newPeriod) =>
                      newPeriod && setPeriod(newPeriod)
                    }
                    sx={{ color: "#193666" }}
                  >
                    {/* <ToggleButton value="monthly">Monthly</ToggleButton> */}
                    <ToggleButton value="yearly">Yearly</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  fullWidth
                  disabled={
                    !userEntries.some(
                      (entry) => entry.email.trim() && entry.modules.length > 0
                    )
                  }
                  sx={{ mt: 1.5 }}
                >
                  Continue to Payment
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Paper className="payment-summary">
            <Typography variant="h6" gutterBottom>
              Payment Summary
            </Typography>
            <Box className="summary-details">
              <Typography>
                Number of users:{" "}
                {userEntries.filter((entry) => entry.email.trim()).length}
              </Typography>
              <Typography>
                Period: {period === "monthly" ? "Monthly" : "Yearly"}
              </Typography>
              <Typography>
                Price per user: $
                {period === "yearly"
                  ? `${
                      bundlePrices[
                        userEntries[currentEditIndex]?.bundles || "freemium"
                      ].yearly
                    }`
                  : `${
                      bundlePrices[
                        userEntries[currentEditIndex]?.bundles || "freemium"
                      ].monthly
                    }`}
              </Typography>
              <Typography variant="h5" className="total-amount">
                Total: ${calculateTotalPrice()}
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowPaymentSummary(false)}
                  size="large"
                  sx={{ flex: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handlePayment}
                  size="large"
                  sx={{ flex: 2 }}
                >
                  Proceed to Payment
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default EnhancedBatchInviteModal;
